import type {
  AnnotationSnapshotV1,
  ImageLabelAnnotationState,
  LabelRasterAnnotationState,
  MaskAnnotationState,
  ModuleCompletionState,
  ScoreAnnotationState,
  TextAnnotationState,
  VectorAnnotationState
} from "@shared/lib/history-types"
import { cloneCoords } from "@virdx/wsi-viewer"
import { create } from "zustand"

interface AnnotationStoreState {
  masks: MaskAnnotationState[]
  vectors: VectorAnnotationState[]
  scores: ScoreAnnotationState[]
  imageLabels: ImageLabelAnnotationState[]
  texts: TextAnnotationState[]
  moduleCompletions: ModuleCompletionState[]
  labelRasters: LabelRasterAnnotationState[]
  mutationSeq: number
  reset: () => void
  replaceFromSnapshot: (snapshot: AnnotationSnapshotV1) => void
  getSnapshot: () => AnnotationSnapshotV1
  upsertMask: (mask: MaskAnnotationState) => void
  removeMask: (id: string) => void
  replaceVectorsForModule: (params: {
    projectModuleId: string
    seriesId: string | null
    vectors: VectorAnnotationState[]
  }) => void
  upsertScore: (score: ScoreAnnotationState) => void
  removeScore: (id: string) => void
  upsertImageLabel: (label: ImageLabelAnnotationState) => void
  removeImageLabel: (id: string) => void
  upsertText: (text: TextAnnotationState) => void
  removeText: (id: string) => void
  setModuleCompletion: (completion: ModuleCompletionState) => void
  upsertLabelRaster: (raster: LabelRasterAnnotationState) => void
  removeLabelRaster: (id: string) => void
}

function cloneLabelRaster(raster: LabelRasterAnnotationState): LabelRasterAnnotationState {
  return {
    ...raster,
    source: { ...raster.source, shape: [...raster.source.shape], chunkShape: [...raster.source.chunkShape] },
    chunks: raster.chunks.map((chunk) => ({
      ...chunk,
      index: [chunk.index[0], chunk.index[1]],
      bbox: [chunk.bbox[0], chunk.bbox[1], chunk.bbox[2], chunk.bbox[3]],
      runs: new Uint8Array(chunk.runs)
    })),
    classesUsed: [...raster.classesUsed]
  }
}

function cloneVector(vector: VectorAnnotationState): VectorAnnotationState {
  return {
    ...vector,
    annotation: {
      ...vector.annotation,
      points: cloneCoords(vector.annotation.points),
      holes: vector.annotation.holes?.map((hole) => cloneCoords(hole)),
      style: { ...vector.annotation.style },
      bbox: { ...vector.annotation.bbox }
    }
  }
}

function upsertById<T extends { id: string }>(items: T[], next: T): T[] {
  const index = items.findIndex((item) => item.id === next.id)
  if (index === -1) return [...items, next]
  const clone = [...items]
  clone[index] = next
  return clone
}

function upsertCompletion(items: ModuleCompletionState[], next: ModuleCompletionState): ModuleCompletionState[] {
  const index = items.findIndex((item) => item.projectModuleId === next.projectModuleId)
  if (index === -1) return [...items, next]
  const clone = [...items]
  clone[index] = next
  return clone
}

export const useAnnotationStore = create<AnnotationStoreState>((set, get) => ({
  masks: [],
  vectors: [],
  scores: [],
  imageLabels: [],
  texts: [],
  moduleCompletions: [],
  labelRasters: [],
  mutationSeq: 0,
  reset: () =>
    set({
      masks: [],
      vectors: [],
      scores: [],
      imageLabels: [],
      texts: [],
      moduleCompletions: [],
      labelRasters: [],
      mutationSeq: 0
    }),
  replaceFromSnapshot: (snapshot) => {
    set({
      masks: snapshot.masks.map((m) => ({
        ...m,
        rle: new Uint8Array(m.rle)
      })),
      vectors: snapshot.vectors,
      scores: snapshot.scores,
      imageLabels: snapshot.imageLabels,
      texts: snapshot.texts,
      moduleCompletions: snapshot.moduleCompletions,
      labelRasters: (snapshot.labelRasters ?? []).map(cloneLabelRaster)
    })
  },
  getSnapshot: () => {
    const state = get()
    return {
      masks: state.masks,
      vectors: state.vectors,
      scores: state.scores,
      imageLabels: state.imageLabels,
      texts: state.texts,
      moduleCompletions: state.moduleCompletions,
      labelRasters: state.labelRasters
    }
  },
  upsertMask: (mask) =>
    set((state) => ({
      masks: upsertById(state.masks, {
        ...mask,
        rle: new Uint8Array(mask.rle)
      }),
      mutationSeq: state.mutationSeq + 1
    })),
  removeMask: (id) =>
    set((state) => ({
      masks: state.masks.filter((item) => item.id !== id),
      mutationSeq: state.mutationSeq + 1
    })),
  replaceVectorsForModule: ({ projectModuleId, seriesId, vectors }) =>
    set((state) => ({
      vectors: [
        ...state.vectors.filter((item) => !(item.projectModuleId === projectModuleId && item.seriesId === seriesId)),
        ...vectors.map((vector) => cloneVector(vector))
      ],
      mutationSeq: state.mutationSeq + 1
    })),
  upsertScore: (score) =>
    set((state) => ({
      scores: upsertById(state.scores, { ...score, values: { ...score.values } }),
      mutationSeq: state.mutationSeq + 1
    })),
  removeScore: (id) =>
    set((state) => ({
      scores: state.scores.filter((item) => item.id !== id),
      mutationSeq: state.mutationSeq + 1
    })),
  upsertImageLabel: (label) =>
    set((state) => ({
      imageLabels: upsertById(state.imageLabels, {
        ...label,
        labels: { ...label.labels },
        seriesMapping: label.seriesMapping
          ? Object.fromEntries(Object.entries(label.seriesMapping).map(([k, v]) => [k, [...v]]))
          : undefined
      }),
      mutationSeq: state.mutationSeq + 1
    })),
  removeImageLabel: (id) =>
    set((state) => ({
      imageLabels: state.imageLabels.filter((item) => item.id !== id),
      mutationSeq: state.mutationSeq + 1
    })),
  upsertText: (text) =>
    set((state) => ({
      texts: upsertById(state.texts, { ...text }),
      mutationSeq: state.mutationSeq + 1
    })),
  removeText: (id) =>
    set((state) => ({
      texts: state.texts.filter((item) => item.id !== id),
      mutationSeq: state.mutationSeq + 1
    })),
  setModuleCompletion: (completion) =>
    set((state) => ({
      moduleCompletions: upsertCompletion(state.moduleCompletions, {
        ...completion
      }),
      mutationSeq: state.mutationSeq + 1
    })),
  upsertLabelRaster: (raster) =>
    set((state) => ({
      labelRasters: upsertById(state.labelRasters, cloneLabelRaster(raster)),
      mutationSeq: state.mutationSeq + 1
    })),
  removeLabelRaster: (id) =>
    set((state) => ({
      labelRasters: state.labelRasters.filter((item) => item.id !== id),
      mutationSeq: state.mutationSeq + 1
    }))
}))
